import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ShoppingBag, Eye, Star, Sparkles } from 'lucide-react';

const ProductCard = ({ product, onAddToCart, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const images = product.images?.length > 0 
    ? product.images.map(img => img.url || img) 
    : [product.featuredImage?.url || 'https://placehold.co/400x500/fce7f3/be185d?text=No+Image'];

  const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price && product.compareAtPrice > 0;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.price / product.compareAtPrice) * 100) 
    : null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: product.currencyCode || 'USD'
    }).format(price);
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart && !isAddingToCart) {
      setIsAddingToCart(true);
      const variant = product.variants?.find(v => v.availableForSale) || product.variants?.[0];
      if (variant) {
        await onAddToCart(product, variant);
      }
      setTimeout(() => setIsAddingToCart(false), 1000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
      whileHover={{ y: -8 }}
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setCurrentImageIndex(0);
      }}
    >
      <Link to={`/products/${product.handle}`} className="block">
        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-gradient-to-br from-pink-50 to-rose-50 shadow-md hover:shadow-xl transition-shadow duration-500">
          {/* Loading Skeleton with shimmer */}
          {!imageLoaded && (
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-pink-100 via-rose-50 to-pink-100"
              animate={{ 
                backgroundPosition: ['200% 0', '-200% 0'],
              }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity,
                ease: "linear"
              }}
              style={{ backgroundSize: '200% 100%' }}
            />
          )}
          
          {/* Main Image */}
          <motion.img
            src={images[currentImageIndex]}
            alt={product.title}
            className={`h-full w-full object-cover transition-all duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            animate={{ 
              scale: isHovered ? 1.1 : 1,
              filter: isHovered ? 'brightness(1.05)' : 'brightness(1)'
            }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />

          {/* Sparkle effect on hover */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 pointer-events-none"
              >
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute"
                    initial={{ 
                      x: Math.random() * 100 + '%', 
                      y: Math.random() * 100 + '%',
                      scale: 0,
                      opacity: 0
                    }}
                    animate={{ 
                      scale: [0, 1, 0],
                      opacity: [0, 1, 0],
                    }}
                    transition={{ 
                      duration: 1.5,
                      delay: i * 0.2,
                      repeat: Infinity,
                    }}
                  >
                    <Sparkles size={12} className="text-white/60" />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hover Image Preview Dots */}
          {images.length > 1 && isHovered && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10"
            >
              {images.slice(0, 4).map((_, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.3 }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentImageIndex === idx 
                      ? 'bg-white scale-125 shadow-lg' 
                      : 'bg-white/60 hover:bg-white/90'
                  }`}
                  onMouseEnter={() => setCurrentImageIndex(idx)}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentImageIndex(idx);
                  }}
                />
              ))}
            </motion.div>
          )}

          {/* Overlay Gradient */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.isNew && (
              <motion.span 
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full shadow-lg"
              >
                NEW
              </motion.span>
            )}
            {product.isBestSeller && (
              <motion.span 
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
                className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full shadow-lg flex items-center gap-1"
              >
                <Star size={10} className="fill-white" /> BEST
              </motion.span>
            )}
            {hasDiscount && (
              <motion.span 
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
                className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-full shadow-lg"
              >
                -{discountPercent}%
              </motion.span>
            )}
            {!product.availableForSale && (
              <motion.span 
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="px-3 py-1 text-xs font-bold bg-gray-800 text-white rounded-full shadow-lg"
              >
                SOLD OUT
              </motion.span>
            )}
          </div>

          {/* Quick Actions */}
          <motion.div 
            className="absolute top-3 right-3 flex flex-col gap-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : 20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.button 
              whileHover={{ scale: 1.2, rotate: 10 }}
              whileTap={{ scale: 0.9 }}
              className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Heart size={18} className="text-pink-500" />
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.2, rotate: -10 }}
              whileTap={{ scale: 0.9 }}
              className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Eye size={18} className="text-pink-500" />
            </motion.button>
          </motion.div>

          {/* Add to Cart Button */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleAddToCart}
              disabled={!product.availableForSale || isAddingToCart}
              className={`w-full py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-xl transition-all ${
                product.availableForSale
                  ? 'bg-white text-pink-600 hover:bg-pink-50'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isAddingToCart ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 border-pink-200 border-t-pink-600 rounded-full"
                />
              ) : (
                <>
                  <ShoppingBag size={18} />
                  {product.availableForSale ? 'Add to Cart' : 'Sold Out'}
                </>
              )}
            </motion.button>
          </motion.div>
        </div>

        {/* Product Info */}
        <motion.div 
          className="mt-4 px-1"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + index * 0.05 }}
        >
          <motion.h3 
            className="font-semibold text-gray-900 group-hover:text-pink-600 transition-colors line-clamp-1 text-lg"
            whileHover={{ x: 3 }}
          >
            {product.title}
          </motion.h3>
          
          {product.productType && (
            <motion.p 
              className="text-sm text-gray-500 mt-0.5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.05 }}
            >
              {product.productType}
            </motion.p>
          )}
          
          <motion.div 
            className="flex items-center gap-2 mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 + index * 0.05 }}
          >
            <motion.span 
              className="text-lg font-bold text-pink-600"
              whileHover={{ scale: 1.05 }}
            >
              {formatPrice(product.price)}
            </motion.span>
            {hasDiscount && (
              <motion.span 
                className="text-sm text-gray-400 line-through"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {formatPrice(product.compareAtPrice)}
              </motion.span>
            )}
          </motion.div>

          {/* Size Preview */}
          {product.sizes && product.sizes.length > 0 && product.sizes[0] !== 'One Size' && (
            <motion.div 
              className="flex gap-1 mt-2 flex-wrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.05 }}
            >
              {product.sizes.slice(0, 5).map((size, i) => (
                <motion.span 
                  key={size} 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.05 }}
                  whileHover={{ scale: 1.1, backgroundColor: '#fce7f3' }}
                  className="px-2 py-0.5 text-xs border border-gray-200 rounded-md text-gray-500 hover:border-pink-300 hover:text-pink-500 transition-colors cursor-pointer"
                >
                  {size}
                </motion.span>
              ))}
              {product.sizes.length > 5 && (
                <span className="px-2 py-0.5 text-xs text-gray-400">+{product.sizes.length - 5}</span>
              )}
            </motion.div>
          )}
        </motion.div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
